import React, { useEffect, useState } from "react";
import {
  FaPlay,
  FaUsers,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaCalendarAlt,
  FaStore,
  FaSyncAlt,
} from "react-icons/fa";
import { axiosMaster, axiosSalary } from "../../api/axiosInstance";

const GenerateAutoMonthlySummary = () => {
  let storedUser = {};

  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  const groupId = storedUser?.groupId;
  const userId = storedUser?.userId;

  const currentDate = new Date();

  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestId, setSelectedRestId] = useState("");

  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [remarks, setRemarks] = useState("Monthly Summary");

  const fetchRestaurants = async () => {
    try {
      const res = await axiosMaster.get(
        `/RestaurantMaster/GetByGroup?groupId=${groupId}`
      );

      const rows = res.data || [];

      setRestaurants(rows);

if (rows?.length) {
  setSelectedRestId((prev) => prev || rows[0].restId);
}
    } catch (err) {
      console.error(err);
    }
  };

 useEffect(() => {
  if (groupId) {
    fetchRestaurants();
  }
}, [groupId]);

  useEffect(() => {
    setSummary(null);
    setStatusFilter("all");
  }, [selectedRestId, month, year]);

 const generateSummary = async () => {
  if (!selectedRestId) {
    alert("Please select a restaurant.");
    return;
  }

  if (!month || !year) {
    alert("Please select month and year.");
    return;
  }

  try {
    setLoading(true);
    setSummary(null);

    const payload = {
      groupId,
      restId: Number(selectedRestId),
      month,
      year,
      userId,
      remarks: remarks?.trim() || "Monthly Summary",
    };

    const res = await axiosSalary.post(
      "/MonthlyAttendanceSummary/GenerateAutoMonthlySummary",
      payload
    );

    setSummary(res?.data || {});
    setStatusFilter("all");
  } catch (err) {
    console.error(err);

    const message =
      err?.response?.data?.message ||
      err?.response?.data ||
      "Failed to generate monthly summary";

    alert(message);
  } finally {
    setLoading(false);
  }
};
  const monthLabel = new Date(year, month - 1, 1).toLocaleString("default", {
    month: "long",
  });

  const restaurantLabel =
    restaurants.find((r) => r.restId === Number(selectedRestId))
      ?.restaurantName || "";

const filteredResults = React.useMemo(() => {
  const results = summary?.results || [];

  return results.filter((row) => {
    if (statusFilter === "success") return row.success;
    if (statusFilter === "skipped") return !row.success && row.isSkipped;
    if (statusFilter === "failed") return !row.success && !row.isSkipped;
    return true;
  });
}, [summary, statusFilter]);

  const statCards = summary
    ? [
        {
          key: "all",
          label: "Total Employees",
          value: summary.totalEmployees,
          icon: <FaUsers />,
          accent: "text-slate-700",
          iconBg: "bg-slate-100 text-slate-600",
          ring: "ring-slate-200",
        },
        {
          key: "success",
          label: "Success",
          value: summary.successCount,
          icon: <FaCheckCircle />,
          accent: "text-emerald-700",
          iconBg: "bg-emerald-100 text-emerald-600",
          ring: "ring-emerald-200",
        },
        {
          key: "skipped",
          label: "Skipped",
          value: summary.skippedCount,
          icon: <FaExclamationTriangle />,
          accent: "text-amber-700",
          iconBg: "bg-amber-100 text-amber-600",
          ring: "ring-amber-200",
        },
        {
          key: "failed",
          label: "Failed",
          value: summary.failedCount,
          icon: <FaTimesCircle />,
          accent: "text-rose-700",
          iconBg: "bg-rose-100 text-rose-600",
          ring: "ring-rose-200",
        },
      ]
    : [];

  return (
   <div className="bg-white rounded-2xl shadow-sm border border-slate-200 min-h-[calc(100vh-120px)]">
  {/* Header */}
  <div className="flex flex-wrap items-start justify-between gap-4 px-6 py-5 border-b border-slate-100">
    <div>
      <h2 className="text-xl font-bold text-slate-800">
        Generate Auto Monthly Summary
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        Generate and review monthly attendance summaries for employees.
      </p>
    </div>
  </div>

  {/* Controls */}
  <div className="px-6 py-5 border-b border-slate-100">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      {/* Restaurant */}
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
          <FaStore className="text-slate-400" />
          Restaurant
        </span>

        <select
          value={selectedRestId}
          onChange={(e) => setSelectedRestId(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {!restaurants?.length && (
            <option value="">No restaurants found</option>
          )}

          {restaurants?.map((r) => (
            <option key={r.restId} value={r.restId}>
              {r.restaurantName}
            </option>
          ))}
        </select>
      </label>

      {/* Month */}
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
          <FaCalendarAlt className="text-slate-400" />
          Month
        </span>

        <select
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {new Date(2025, i, 1).toLocaleString("default", {
                month: "long",
              })}
            </option>
          ))}
        </select>
      </label>

      {/* Year */}
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-slate-500">Year</span>

        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {[2025, 2026, 2027, 2028].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </label>

      {/* Remarks */}
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-slate-500">
          Remarks
        </span>

        <input
          type="text"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Monthly Summary"
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </label>

      {/* Generate Button */}
      <div className="flex items-end">
        <button
          onClick={generateSummary}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium"
        >
          {loading ? (
            <>
              <FaSyncAlt className="animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <FaPlay className="text-xs" />
              Generate
            </>
          )}
        </button>
      </div>
    </div>
  </div>

  {/* Body */}
  <div className="px-6 py-5">
    {!summary && !loading && (
      <div className="flex flex-col items-center justify-center text-center py-20 text-slate-400">
        <FaUsers className="text-4xl mb-3 opacity-40" />
        <p className="text-sm">
          Select a restaurant, month and year, then click Generate.
        </p>
      </div>
    )}

    {loading && (
      <div className="flex flex-col items-center justify-center text-center py-20 text-slate-400">
        <FaSyncAlt className="text-3xl mb-3 animate-spin text-blue-400" />
        <p className="text-sm">
          Generating summary for{" "}
          <strong>{restaurantLabel || "Selected Restaurant"}</strong>
          {" - "}
          {monthLabel} {year}
        </p>
      </div>
    )}

    {summary && !loading && (
      <>
        {/* Summary Info */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-slate-500">Restaurant</p>
              <p className="font-semibold">{restaurantLabel}</p>
            </div>

            <div>
              <p className="text-xs text-slate-500">Period</p>
              <p className="font-semibold">
                {monthLabel} {year}
              </p>
            </div>

            <div>
              <p className="text-xs text-slate-500">Remarks</p>
              <p className="font-semibold">{remarks}</p>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {statCards?.map((card) => (
            <button
              key={card.key}
              onClick={() => setStatusFilter(card.key)}
              className={`text-left p-4 rounded-xl border transition ring-1 ${card.ring}
              ${
                statusFilter === card.key
                  ? "border-slate-300 shadow-sm bg-white"
                  : "border-transparent bg-slate-50 hover:bg-white"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${card.iconBg}`}
                >
                  {card.icon}
                </span>
              </div>

              <div className="text-xs text-slate-500">
                {card.label}
              </div>

              <div className={`text-2xl font-bold ${card.accent}`}>
                {card.value}
              </div>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-auto max-h-[500px]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left">Emp Id</th>
                  <th className="px-4 py-3 text-left">Employee Name</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Message</th>
                </tr>
              </thead>

              <tbody>
                {!filteredResults?.length ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center py-8 text-slate-400"
                    >
                      No records found.
                    </td>
                  </tr>
                ) : (
                  filteredResults.map((row, index) => (
                    <tr key={index} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-3">{row.empId}</td>
                      <td className="px-4 py-3 font-medium">
                        {row.empName}
                      </td>
                      <td className="px-4 py-3">
                        {row.success
                          ? "✅ Success"
                          : row.isSkipped
                          ? "⚠️ Skipped"
                          : "❌ Failed"}
                      </td>
                      <td className="px-4 py-3">
                        {row.message}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    )}
  </div>
</div>
  );
};

export default GenerateAutoMonthlySummary;